import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDebounce } from "@hooks/useDebounce";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { autoFocusInput } from "@util/InputUtil";
import { TextInputComponents } from "./TextInputComponents";

const TextDisplayWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: baseline;
    min-width: fit-content;
    width: 100%;

    min-height: 20px;
`;

const TextInput = styled.input<{ isEditing: boolean }>`
    display: ${(props) => (props.isEditing ? "block" : "none")};
    font-size: ${FontSize.normal};
    border: none;
    border-radius: ${Styles.borderRadiusExtraSharp};
    width: 100%;
    cursor: inherit;
    background-color: ${(props) => props.theme.backgroundPrimary};

    &:focus,
    &:focus-visible {
        outline: none;
        font-size: ${FontSize.normal};
        font-family: inherit;
        color: ${(props) => props.theme.textColorPrimary};
    }

    &:disabled {
        background-color: ${(props) => props.theme.backgroundSpecial};
    }
`;

const TextDisplay = styled.span<{ isEditing: boolean; isDisabled: boolean }>`
    display: ${(props) => (props.isEditing ? "none" : "block")};
    width: 100%;

    color: ${(props) => (props.isDisabled ? props.theme.textColorSecondary : props.theme.textColorPrimary)};
    font-size: ${FontSize.normal};
`;

interface LifeAtTextInputProps {
    readonly textValue: string;
    readonly isEditing: boolean;
    readonly finishEditCallback: () => void;
    readonly flagSaveEdit?: boolean;
    readonly handleUpdate?: (editValue: string) => void;
    readonly placeholder?: string;
    readonly minLength?: number;
    readonly maxLength?: number;
    readonly label?: string;
    readonly showBorder?: boolean;
    readonly errorMessage?: string;
    readonly isDisabled?: boolean;
    // If the parent state should update when the input value changes
    readonly updateOnChange?: boolean;
}
export default function LifeAtTextInput(props: LifeAtTextInputProps) {
    const [inputVal, setInputVal] = useState(props.textValue);

    // If the value changes from the parent, we should update the input value to display
    // the correct contents. E.g. user edits to-do list name in menubar window, we need to
    // have the to-do list names in the selector dropdown update in the main app window.
    useEffect(() => {
        if (props.textValue && props.textValue !== inputVal) {
            setInputVal(props.textValue);
        }
    }, [props.textValue]);

    useEffect(() => {
        if (props.flagSaveEdit && props.isEditing) {
            saveEdit(inputVal);
        }
    }, [props.flagSaveEdit]);

    const debouncedUpdate = useDebounce((updatedInputValue: string) => {
        if (props.updateOnChange) {
            props.handleUpdate?.(updatedInputValue);
        }
    }, 250);

    const handleEditText = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputVal(e.target.value);
        debouncedUpdate(e.target.value);
    };

    function saveEdit(updatedInputValue: string) {
        if (inputVal !== props.textValue) {
            props.finishEditCallback();
            props.handleUpdate?.(updatedInputValue);
        } else {
            cancelEdit();
        }
    }

    const cancelEdit = () => {
        setInputVal(props.textValue);
        props.finishEditCallback();
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            saveEdit(e.currentTarget.value);
        } else if (e.key === "Escape") {
            cancelEdit();
        }
    };

    return (
        <TextInputComponents.TextAreaWrapper
            label={props.label}
            showBorder={props.showBorder}
            errorMessage={props.errorMessage}
            isDisabled={props.isDisabled}
        >
            <TextDisplayWrapper>
                <TextDisplay isEditing={props.isEditing} isDisabled={props.isDisabled}>
                    {inputVal}
                </TextDisplay>

                {props.isEditing && (
                    <TextInput
                        type="text"
                        name={`lifeat-text-input-edit-${props.textValue}`}
                        value={inputVal}
                        placeholder={props.placeholder}
                        autoComplete={"off"}
                        autoFocus={true}
                        ref={(input) => autoFocusInput(input)}
                        minLength={props.minLength}
                        maxLength={props.maxLength}
                        onChange={handleEditText}
                        onBlur={(e) => saveEdit(e.target.value)}
                        onKeyDown={handleKeyPress}
                        isEditing={props.isEditing}
                        disabled={props.isDisabled}
                    />
                )}
            </TextDisplayWrapper>
        </TextInputComponents.TextAreaWrapper>
    );
}
